import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios';

import { ROUTES } from '@/components/Shared/routes/routes';
import { SqlExecutorApi } from '@/integrations/adminApi/sqlExecutorApi';
import { DownloadFilesApi } from '@/integrations/downloadFilesApi';
import { abortControllerManager } from '@/services/helpers/api';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import { getNavigate } from '@/services/helpers/navigate-helper';
import { getUserToken, setUserToken } from '@/services/user-hash';

// Расширяем InternalAxiosRequestConfig для хранения AbortController
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  __abortController?: AbortController;
  __isRetry?: boolean;
}

let refreshTokenPromise: Promise<
  AxiosResponse<{ accessToken: string; refreshToken: string }>
> | null = null;

const logout = () => {
  const navigate = getNavigate();
  clearLocalStorageHelper();
  const savedUrl =
    window.location.pathname !== ROUTES.login &&
    !window.location.pathname.includes('create')
      ? `${window.location.pathname}${window.location.search}`
      : '';
  navigate(ROUTES.login, { state: { savedUrl } });
};

const refreshOnce: () => Promise<
  AxiosResponse<{ accessToken: string; refreshToken: string }>
> = async () => {
  if (!refreshTokenPromise) {
    const { refreshToken } = getUserToken();
    refreshTokenPromise = axios({
      url: `${process.env.REACT_APP_AUTH_URL}/auth/refresh`,
      method: 'get',
      withCredentials: false,
      headers: {
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${refreshToken}`
      }
    })
      .then((response) => {
        const { accessToken, refreshToken } = response.data;
        if (!accessToken || accessToken === 'undefined') {
          throw new Error('Invalid refresh response');
        }
        const { emailAllur } = getUserToken();
        setUserToken({ accessToken, refreshToken, emailAllur });
        return response;
      })
      .catch((e) => {
        // 401 от рефреша или пустой токен — сессия мертва
        if (!axios.isAxiosError(e) || e.response?.status === 401) logout();
        throw e;
      });
  }
  return refreshTokenPromise;
};

export const createIndexApi = (baseUrl?: string): AxiosInstance => {
  const indexApi = axios.create({
    baseURL: baseUrl || `${process.env.REACT_APP_AUTH_URL}`
  });

  indexApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const accessToken = getUserToken().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    // Если signal уже передан явно, используем его
    if (!config.signal) {
      const controller = abortControllerManager.createController();
      config.signal = controller.signal;
      // Сохраняем controller в config для последующего удаления
      (config as ExtendedAxiosRequestConfig).__abortController = controller;
    }
    return config;
  });

  indexApi.interceptors.response.use(
    async (response) => {
      const controller = (response.config as ExtendedAxiosRequestConfig)
        .__abortController;
      if (controller) {
        abortControllerManager.removeController(controller);
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as
        ExtendedAxiosRequestConfig | undefined;
      if (originalRequest?.__abortController) {
        abortControllerManager.removeController(
          originalRequest.__abortController
        );
      }

      if (!originalRequest || error.response?.status !== 401) {
        return Promise.reject(error);
      }

      // Повтор со свежим токеном снова получил 401
      if (originalRequest.__isRetry) {
        logout();
        return Promise.reject(error);
      }

      originalRequest.__isRetry = true;
      try {
        await refreshOnce();
      } catch {
        // logout (если нужен) уже вызван внутри refreshOnce — ровно один раз
        return Promise.reject(error);
      }
      return indexApi(originalRequest);
    }
  );

  return indexApi;
};

export const indexApi = createIndexApi();

export const downloadFilesApi = new DownloadFilesApi(indexApi);
export const sqlExecutor = new SqlExecutorApi(indexApi);
